import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ToastService } from '../../../services/toast.service'
import {
    getSiteIdFromUrl,
    SiteDetailsResponse,
    updateSiteContacts,
} from '../services/copSiteDetails.service'
import { ContactField, ContactSide } from './ContactField'
import {
    ContactFormState,
    ContactsConfig,
} from './EditContactsModal.types'
import {
    buildContactPayload,
    toFormState,
    validateFields,
} from './editContactsValidation'

export type {
    ContactFieldConfig,
    ContactsConfig,
    UnsavedChangesConfig,
} from './EditContactsModal.types'

type EditContactsModalProps = {
    config: ContactsConfig
    siteData: SiteDetailsResponse | null
    onClose: () => void
    onSaved: (updated: Pick<SiteDetailsResponse, 'primaryContact' | 'secondaryContact'>) => void
}

const DEFAULT_DIALOG_TITLE = 'Edit Contacts'
const DEFAULT_PRIMARY_LABEL = 'Primary Contact'
const DEFAULT_SECONDARY_LABEL = 'Secondary Contact'
const DEFAULT_SAVE_LABEL = 'Save'
const DEFAULT_CANCEL_LABEL = 'Cancel'
const DEFAULT_SUCCESS_MSG = 'Site contacts has been successfully updated'
const DEFAULT_INVALID_MSG = 'Please enter a valid {field}'
const SAVE_FAILURE_MSG = 'Failed to save contacts. Please try again.'
const DEFAULT_UNSAVED_MESSAGE = 'Are you sure you want to exit?'
const DEFAULT_UNSAVED_SUBTEXT = 'Your changes will not be saved.'
const DEFAULT_UNSAVED_YES = 'Yes'
const DEFAULT_UNSAVED_NO = 'No'

export const EditContactsModal: React.FC<Readonly<EditContactsModalProps>> = ({
    config,
    siteData,
    onClose,
    onSaved,
}) => {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const unsavedDialogRef = useRef<HTMLDialogElement>(null)

    const {
        dialogTitle = DEFAULT_DIALOG_TITLE,
        primaryContactLabel = DEFAULT_PRIMARY_LABEL,
        secondaryContactLabel = DEFAULT_SECONDARY_LABEL,
        saveButtonLabel = DEFAULT_SAVE_LABEL,
        cancelButtonLabel = DEFAULT_CANCEL_LABEL,
        successMessage = DEFAULT_SUCCESS_MSG,
        emailInvalidMessage = DEFAULT_INVALID_MSG,
        phoneInvalidMessage = DEFAULT_INVALID_MSG,
        fields = [],
        unsavedChanges,
    } = config

    const [primaryForm, setPrimaryForm] = useState<ContactFormState>(() =>
        toFormState(siteData?.primaryContact, fields)
    )
    const [secondaryForm, setSecondaryForm] = useState<ContactFormState>(() =>
        toFormState(siteData?.secondaryContact, fields)
    )
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [isSaving, setIsSaving] = useState(false)
    const [isDirty, setIsDirty] = useState(false)
    const [showUnsaved, setShowUnsaved] = useState(false)

    const unsavedMessage = unsavedChanges?.message ?? DEFAULT_UNSAVED_MESSAGE
    const unsavedSubtext = unsavedChanges?.subtext ?? DEFAULT_UNSAVED_SUBTEXT
    const unsavedYesLabel = unsavedChanges?.yesLabel ?? DEFAULT_UNSAVED_YES
    const unsavedNoLabel = unsavedChanges?.noLabel ?? DEFAULT_UNSAVED_NO
    const saveButtonText = isSaving ? '...' : saveButtonLabel
    const isSaveDisabled = isSaving || !isDirty

    useEffect(() => {
        dialogRef.current?.showModal()
        return () => { dialogRef.current?.close() }
    }, [])

    // Re-hydrate form when siteData arrives/changes, unless the user has edits in progress.
    useEffect(() => {
        if (isDirty) return
        setPrimaryForm(toFormState(siteData?.primaryContact, fields))
        setSecondaryForm(toFormState(siteData?.secondaryContact, fields))
    }, [siteData?.primaryContact, siteData?.secondaryContact, fields, isDirty])

    useEffect(() => {
        if (showUnsaved) unsavedDialogRef.current?.showModal()
        else unsavedDialogRef.current?.close()
    }, [showUnsaved])

    const handleFieldChange = useCallback(
        (side: ContactSide, key: string, value: string): void => {
            const setter = side === 'primary' ? setPrimaryForm : setSecondaryForm
            setter(prev => ({ ...prev, [key]: value }))
            setIsDirty(true)
            setErrors(prev => {
                const next = { ...prev }
                delete next[`${side}_${key}`]
                return next
            })
        },
        [],
    )

    const handleCancel = useCallback((): void => {
        if (isDirty) setShowUnsaved(true)
        else onClose()
    }, [isDirty, onClose])

    const handleSave = useCallback(async (): Promise<void> => {
        const validationErrors = validateFields({
            fields,
            primary: primaryForm,
            secondary: secondaryForm,
            emailInvalidMsg: emailInvalidMessage,
            phoneInvalidMsg: phoneInvalidMessage,
        })
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors)
            return
        }

        const siteId = getSiteIdFromUrl()
        if (!siteId) return

        setIsSaving(true)
        try {
            const payload = {
                primaryContact: buildContactPayload(primaryForm, siteData?.primaryContact, fields),
                secondaryContact: buildContactPayload(secondaryForm, siteData?.secondaryContact, fields),
            }
            await updateSiteContacts(siteId, payload)
            ToastService.success(successMessage)
            onSaved({ primaryContact: payload.primaryContact, secondaryContact: payload.secondaryContact })
            onClose()
        } catch {
            ToastService.error(SAVE_FAILURE_MSG)
        } finally {
            setIsSaving(false)
        }
    }, [fields, primaryForm, secondaryForm, emailInvalidMessage, phoneInvalidMessage, siteData, onSaved, onClose, successMessage])

    const handleSaveClick = useCallback((): void => { void handleSave() }, [handleSave])
    const handleDialogCancel = useCallback(
        (e: React.SyntheticEvent<HTMLDialogElement>): void => {
            e.preventDefault()
            handleCancel()
        },
        [handleCancel],
    )
    const handleCloseUnsaved = useCallback((): void => setShowUnsaved(false), [])
    const handleUnsavedDialogCancel = useCallback(
        (e: React.SyntheticEvent<HTMLDialogElement>): void => {
            e.preventDefault()
            setShowUnsaved(false)
        },
        [],
    )

    const primaryFields = useMemo(
        () => fields.map(field => (
            <ContactField
                key={`primary-${field.fieldKey}`}
                field={field}
                side="primary"
                value={primaryForm[field.fieldKey] ?? ''}
                errorMessage={errors[`primary_${field.fieldKey}`]}
                disabled={isSaving}
                onChange={handleFieldChange}
            />
        )),
        [fields, primaryForm, errors, isSaving, handleFieldChange],
    )

    const secondaryFields = useMemo(
        () => fields.map(field => (
            <ContactField
                key={`secondary-${field.fieldKey}`}
                field={field}
                side="secondary"
                value={secondaryForm[field.fieldKey] ?? ''}
                errorMessage={errors[`secondary_${field.fieldKey}`]}
                disabled={isSaving}
                onChange={handleFieldChange}
            />
        )),
        [fields, secondaryForm, errors, isSaving, handleFieldChange],
    )

    return (
        <>
            <dialog
                ref={dialogRef}
                className="edit-contacts-modal"
                aria-labelledby="edit-contacts-title"
                aria-modal="true"
                onClose={onClose}
                onCancel={handleDialogCancel}
            >
                <div className="edit-contacts-modal__header">
                    <h2 id="edit-contacts-title" className="edit-contacts-modal__title">
                        {dialogTitle}
                    </h2>
                    <button
                        type="button"
                        className="edit-contacts-modal__close"
                        aria-label="Close dialog"
                        onClick={handleCancel}
                        disabled={isSaving}
                    >
                        &#x2715;
                    </button>
                </div>

                <div className="edit-contacts-modal__body">
                    <div className="edit-contacts-modal__column">
                        <div className="edit-contacts-modal__column-heading">{primaryContactLabel}</div>
                        {primaryFields}
                    </div>
                    <div className="edit-contacts-modal__column">
                        <div className="edit-contacts-modal__column-heading">{secondaryContactLabel}</div>
                        {secondaryFields}
                    </div>
                </div>

                <div className="edit-contacts-modal__footer">
                    <button
                        type="button"
                        className="edit-contacts-modal__btn edit-contacts-modal__btn--cancel"
                        onClick={handleCancel}
                        disabled={isSaving}
                    >
                        {cancelButtonLabel}
                    </button>
                    <button
                        type="button"
                        className="edit-contacts-modal__btn edit-contacts-modal__btn--save"
                        onClick={handleSaveClick}
                        disabled={isSaveDisabled}
                    >
                        {saveButtonText}
                    </button>
                </div>
            </dialog>

            {showUnsaved && (
                <dialog
                    ref={unsavedDialogRef}
                    className="edit-contacts-modal__unsaved"
                    aria-modal="true"
                    onCancel={handleUnsavedDialogCancel}
                >
                    <div className="edit-contacts-modal__header">
                        <h3 className="edit-contacts-modal__title">
                            {unsavedMessage}
                        </h3>
                        <button
                            type="button"
                            className="edit-contacts-modal__close"
                            aria-label="Close"
                            onClick={handleCloseUnsaved}
                        >
                            &#x2715;
                        </button>
                    </div>
                    <p className="edit-contacts-modal__unsaved-sub">
                        {unsavedSubtext}
                    </p>
                    <div className="edit-contacts-modal__footer">
                        <button
                            type="button"
                            className="edit-contacts-modal__btn edit-contacts-modal__btn--cancel"
                            onClick={handleCloseUnsaved}
                        >
                            {unsavedNoLabel}
                        </button>
                        <button
                            type="button"
                            className="edit-contacts-modal__btn edit-contacts-modal__btn--save"
                            onClick={onClose}
                        >
                            {unsavedYesLabel}
                        </button>
                    </div>
                </dialog>
            )}
        </>
    )
}
